import { Category } from "../models/Category";
import { Deserializer } from "jsonapi-serializer";
import createDebug from "debug";
import { APIConnector } from "./api/APIConnector";
import { genericError } from "../models/GenericError";
import settingTypesBasicFields from "../constants/setting-types-basic-fields";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { PaginationModel } from "../models/Pagination";
import { createPagination } from "./utilities/paginationBuilder";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { DEF_PG_SIZE_EXTRA_LRG } from '../constants/maximum-page-size';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { PRESET_TYPES, CATEGORIES } from "../constants/preset-constants";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { isSuccessStatus } from "../utilities/requestResponseValidator";

const debug = createDebug("category-service");
const apiConnector = new APIConnector();
const displayNameId = `${settingTypesBasicFields.Display_Name.id}`;
const UID_Id = `${settingTypesBasicFields.Unique_Identifier.id}`;

export const deleteCategory = async (categoryId: number): Promise<DeleteEntityResult> => {
    try {
        const url = `profile/${categoryId}`;
        const door = {
            data: {
                type: "profile",
                id: categoryId
            }
        }
        const response = await apiConnector.deleteData(url, door)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: categoryId.toString(), success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: categoryId.toString(), success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCategoryById = async (categoryId: number): Promise<Category> => {
    try {
        const includes = "include=inheritsFrom,site";
        const profileFields = "fields[profile]=trnName,pDesc,inheritsFrom,profileType,site";
        const url = `profile/${categoryId}?${includes}&${profileFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {

            if (json.errors) { // Show errors from backend
                return reject({ status: response.status, success: false, message: getConstraintViolation(json.errors[0].detail) })
            }
            else if (isSuccessStatus(response.status) === false) { // No specific errors but failed status code.
                return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
            }

            new Deserializer().deserialize(json, (error: any, apiCategory: any) => {
                if (error) {
                    return reject({ status: response.status, success: false, messageKey: TranslationKeys.readServerResponseFailed });
                }
                else {
                    const category: Category = {
                        id: categoryId,
                        displayName: apiCategory['trn-name'],
                        heading: apiCategory['trn-name'],
                        description: apiCategory['p-desc'],
                        inheritsFromId: apiCategory['inherits-from']?.['id'],
                        siteId: apiCategory['site']?.['id'],
                        value: categoryId,
                    }

                    return resolve(category);
                }
            });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export async function getCategories(): Promise<Category[]> {
    const profileFields = "fields[profile]=trnName,totalMasters,profileType";
    const filterByProfileType = `filter=profileType.id==${PRESET_TYPES.TYPE_CATEGORY}`;
    const pageSizeMax = "page[size]=100";
    const url = `profile?${filterByProfileType}&${profileFields}&${pageSizeMax}`;
    const response = await apiConnector.fetchData(url);
    const json = await response.json();

    const categories: Category[] = await new Promise<Category[]>((resolve, reject) => {
        if (json.errors) {
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
        } else {
            new Deserializer().deserialize(json, async (err: any, profiles: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const categories: Category[] = await Promise.all(profiles.map(async (p: any) => (
                    {
                        value: p.id,
                        displayName: p["trn-name"],
                        heading: p["trn-name"],
                        id: p.id,
                        count: p["total-masters"],
                        menuDetailsId: (await buildCategoriesData(p.id)) // TODO: If this is to get the name the user renamed to, can we not just include this with the profile lookup instead? Reduce the api call's when loading enrolment list.
                    })));

                return resolve(categories);
            });
        }
    });
    return categories;
}

export async function getMenuDetailByProfileId(profileId: string): Promise<Name[]> {
    const menuDetailFields = "fields[menuDetail]=settingType";
    const profileFields = "fields[profile]=trnName";
    const settingTypeFields = "fields[settingType]=trnName";
    const include = "include=profile,settingType";
    const filterByProfile = `filter=profile.id==${profileId}`;
    const pageSizeMax = "page[size]=100";
    const url = `menuDetail?${include}&${menuDetailFields}&${filterByProfile}&${profileFields}&${settingTypeFields}&${pageSizeMax}`;
    const response = await apiConnector.fetchData(url);
    const json = await response.json();

    const categories: Name[] = await new Promise<Name[]>((resolve, reject) => {
        if (json.errors) {
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
        } else {
            new Deserializer().deserialize(json, (err: any, menus: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const categories: Name[] = menus.map((menu: any) => ({
                    value: menu.id,
                    label: menu['setting-type']['id']
                }));
                return resolve(categories);
            });
        }
    });
    return categories;
}

const buildCategoriesData = async (profiles) => {
    const getMenuDetail = await getMenuDetailByProfileId(profiles);
    return getMenuDetail[0].label;
}

export interface CategoryRequest {
    id?: number,
    displayName: string;
    description: string;
    copyOf: string; //Profile Id
    singleOwner: boolean; //Belongs to a single owner at a time
    autoGenerateId: boolean;
    fields: string[];
    siteId: string;
    oldFields?: string[];
}

export const saveCategory = async (category: CategoryRequest) => {
    const updateCategory = category.id && category.id > 0;
    const url = `profile${updateCategory ? `/${category.id}` : ''}`;
    const defaultEmptyProfileTemplateId = 35;
    const inheritsFrom = updateCategory
        ? undefined
        : {
            inheritsFrom: {
                data: {
                    type: "profile",
                    id: defaultEmptyProfileTemplateId
                }
            }
        };
    const inheritsFromId = updateCategory
        ? undefined
        : { inheritsFromId: category.copyOf }

    const newProfile = {
        data: {
            id: updateCategory ? category.id : 0,
            type: "profile",
            attributes: {
                // pAlias: "",
                trnName: category.displayName,
                // profileInheritsFrom: newCategory.copyOf,
                pDesc: category.description,
                // pHidden: 0,
                // autoGenerateId: newCategory.autoGenerateId,
                // singleOwner: newCategory.singleOwner
                pSysname: "USER_DEFINED",
                // pSysnameType: ""
                ...inheritsFromId
            },
            relationships: {
                profileType: {
                    // hardcoded to Person currently
                    data: {
                        type: "profileType",
                        id: "3"
                    }
                },
                site: {
                    data: {
                        type: "site",
                        id: 1 // Categories can only be created at the global level, so site is hardcoded to 1
                    }
                },
                ...inheritsFrom
            }
        }
    }

    try {
        const response = await apiConnector.sendData(url, (updateCategory === false), newProfile);
        const json = response.status === NO_CONTENT ? { data: { id: category.id } } : await response.json();

        return new Promise(async (resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            else {
                const newProfileId = json['data']['id'];

                // delete removed fields:
                const removedFields = category.oldFields
                    ? category.oldFields?.filter(field => category.fields.includes(field) === false)
                    : undefined;

                // create added fields:
                const addedFields = category.oldFields
                    ? category.fields.concat(addRequiredFields(category)).filter(field => category.oldFields?.includes(field) === false)
                    : category.fields.concat(addRequiredFields(category));

                const deleteSucceeded = (removedFields && (removedFields as Array<any>).length <= 0)
                    ? true
                    : await deleteProfileFields(newProfileId, removedFields);

                const addSucceeded = addedFields.length > 0
                    ? await addProfileFields(newProfileId, addedFields, resolve, reject)
                    : true;

                if (deleteSucceeded === false) {
                    return reject({ status: response.status, id: newProfileId, success: false, messageKey: TranslationKeys.partialSuccessFldSave });
                }
                else if (addSucceeded === false) {
                    return reject({ status: response.status, id: newProfileId, success: false, messageKey: TranslationKeys.partialSuccessFldSave });
                }
                else {
                    return resolve({ id: newProfileId, success: true });
                }
            }
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

const deleteProfileFields = async (newProfileId: any, fieldIds) => {
    try {
        let pagination: PaginationModel = {
            pageNumber: 1,
            maxPageSize: DEF_PG_SIZE_EXTRA_LRG,
            totalCount: 0,
            totalPages: 1
        };
        let attemptCount = 0;
        const maxAttemptCount = 3;
        do {
            try {
                // delete all fields on category
                const deleteResult: any = await deleteProfileFieldsPage(newProfileId, pagination, fieldIds);
                pagination = {
                    ...pagination,
                    pageNumber: deleteResult.pagination.pageNumber,
                    totalPages: deleteResult.pagination.totalPages
                };
            }
            catch (error) {
                attemptCount = attemptCount + 1;
            }
        } while (pagination.totalPages > 0
            && attemptCount < maxAttemptCount);

        const deleteSucceeded = attemptCount < maxAttemptCount;
        return deleteSucceeded;
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const deleteProfileFieldsPage = async (profileId, pagination: PaginationModel, fieldIds) => {
    try {
        const filters = createPartialURL(pagination.pageNumber, 100);
        const fetchProfileFieldsUrl = `profileFields?filter=profile.id==${profileId}${fieldIds && fieldIds.length > 0 ? `;settingType.id=in=(${fieldIds.join(',')})` : ''}${filters}`;
        const profileFieldsResponse = await apiConnector.fetchData(fetchProfileFieldsUrl);

        return new Promise(async (resolve, reject) => {
            if (profileFieldsResponse && isSuccessStatus(profileFieldsResponse.status)) {
                const json = await profileFieldsResponse.json()

                if (json.errors) {
                    return reject({ status: profileFieldsResponse.status, id: profileId, success: false, message: getConstraintViolation(json.errors[0].detail) })
                }
                else {
                    const newPagination = createPagination(json.meta);
                    const deletePromises = json["data"].map(async (profileFieldToDelete) => {
                        const profileFieldToDeleteId = profileFieldToDelete["id"];
                        const profileFieldDeleteUrl = `setting/${profileFieldToDeleteId}`;
                        const newProfileFieldToDelete = { data: { id: profileFieldToDeleteId, type: "setting" } };
                        const deleteProfileFieldResponse = await apiConnector.deleteData(profileFieldDeleteUrl, newProfileFieldToDelete);
                        return new Promise((resolve, reject) => {
                            if (deleteProfileFieldResponse && isSuccessStatus(deleteProfileFieldResponse.status)) {
                                return resolve({ id: profileFieldToDeleteId, success: true });
                            }
                            else {
                                return reject({ status: deleteProfileFieldResponse.status, id: profileFieldToDeleteId, success: false, messageKey: TranslationKeys.error });
                            }
                        })
                    });

                    await Promise.all(deletePromises)
                        .then(value => {
                            return resolve({ id: profileId, success: true, pagination: newPagination });
                        })
                        .catch(error => {
                            return reject({ status: INTERNAL_SERVER_ERROR, id: profileId, success: false });
                        });
                }
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const addProfileFields = async (newProfileId: any, selectedFieldIds, resolve: (value: unknown) => void, reject: (value: unknown) => void) => {
    try {
        const fieldIds = selectedFieldIds;

        // fetch profile fields to copy
        const includes = "include=settingType,profile";
        const settingFields = "fields[setting]=trnName,settingEventSysname,profile,settingType";
        const profileFields = "fields[profile]=trnName,profileType";
        const filterByProfiles = `filter=profile.id=in=(${CATEGORIES.ID_SYSTEM + ',' + CATEGORIES.ID_DEFAULT_TEMPLATE})`;
        const filterBySettings = `settingType.id=in=(${fieldIds.join(',')})`;
        const pageSizeMax = "page[size]=100";
        const fetchProfileFieldUrl = `setting?${includes}&${filterByProfiles};${filterBySettings}&${settingFields}&${profileFields}&${pageSizeMax}`;
        const copyProfileFieldsResponse = await apiConnector.fetchData(fetchProfileFieldUrl);

        if (copyProfileFieldsResponse && isSuccessStatus(copyProfileFieldsResponse.status)) {
            const copyProfileFields = await copyProfileFieldsResponse.json();

            let newProfileFieldReponses = new Array(0);
            if (copyProfileFields && !(copyProfileFields.error)) {
                const sendProfileFieldUrl = 'setting';
                const newProfileFields = Object.values<any>(copyProfileFields['data']
                    .reduce((resultList, currentSetting, index) => {
                        const columnId = currentSetting['relationships']?.['settingType']?.['data']?.['id'];
                        if (!!columnId && resultList[columnId] === undefined) {
                            return {
                                ...resultList,
                                [columnId]: currentSetting
                            }
                        }
                        return resultList;
                    }, {}))
                    .map(element => {
                        let newElement = element["attributes"];
                        const propToBeDeleted = 'settingEventSysname';
                        delete newElement[propToBeDeleted];
                        return {
                            data: {
                                type: "setting",
                                id: 0,
                                attributes: {
                                    ...newElement,
                                    settingIdToCopy: element["id"]
                                },
                                relationships: {
                                    ...element['relationships'],
                                    profile: {
                                        data: {
                                            type: "profile",
                                            id: newProfileId
                                        }
                                    }
                                }
                            }
                        };
                    });
                newProfileFieldReponses = newProfileFields.map(newProfileField => {
                    return apiConnector.sendData(sendProfileFieldUrl, true, newProfileField);
                });
            }

            const results = await Promise.all(newProfileFieldReponses);
            const failures = results.filter(result => isSuccessStatus(result.status) === false);
            return failures.length === 0;
        }
        else {
            return false;
        }
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const addRequiredFields = (currentCategory) => {
    const adjustedFields = new Array(0);

    if (currentCategory.fields.includes(displayNameId) === false) {
        adjustedFields.push(displayNameId);
    }
    if (currentCategory.fields.includes(UID_Id) === false) {
        adjustedFields.push(UID_Id);
    }

    return adjustedFields;
}

export const getCategoryNameWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const profileFields = "fields[profile]=trnName,isDefault,profileSysnameType,totalMasters,profileType";
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterByProfileType = `filter=profileType.id==${PRESET_TYPES.TYPE_CATEGORY}`;
        const updatedFilters = filter.length > 0
            ? filter.replace("filter=", `${filterByProfileType};`)
            : `&${filterByProfileType}`

        const url = `profile?${profileFields}&` + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, updatedFilters);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const readerProfiles: Name[] = items.map((readerProfile): Name => {
                    return {
                        value: readerProfile['id'],
                        label: readerProfile['trn-name'],
                        isDefault: readerProfile['is-default'],
                        elementSysname: readerProfile['profile-sysname-type']
                    };
                });

                return resolve({ rows: readerProfiles, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
